import { useEffect, useState } from "react";
import { useCheckAuth, useLogin, useLogout } from "react-admin";
import { Card } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { LogoAvatar } from "../assets/images/svg";
import { PasswordInput } from "../components/ui/input";
import { Subtitle, Title } from "../components/ui/title";
import { ThemedPrimaryButton } from "../components/ui/button";
import { FormField } from "../autoOnboarding/components/FormField";
import { Error } from "../autoOnboarding/components/Error";
import { validateEmail } from "../utils/AutoOnboarding";
import ERROR_CODES from "../common/ErrorCodes";
import { ROLE_TYPES } from "../enums/Roles";
import { LightSurface } from "../common/LightSurface";

const formSchema = z.object({
  email: z
    .string()
    .min(1, "El correo electrónico es obligatorio")
    .refine(validateEmail, {
      message: "El correo electrónico no es válido",
    }),
  password: z.string().min(1, "La contraseña es obligatoria"),
});

const CustomLogin = () => {
  const [loading, setLoading] = useState(false);
  const checkAuth = useCheckAuth();
  const navigate = useNavigate();
  const login = useLogin();
  const logout = useLogout();
  const [error, setError] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });
  const [foreceReload, setForeceReload] = useState(false);

  useEffect(() => {
    checkAuth()
      .then(async () => {
        const role = localStorage.getItem("role");
        if (role === ROLE_TYPES.RESTAURANT_DRAFT) {
          navigate("/onboarding");
        } else if (role === ROLE_TYPES.RESTAURANT) {
          navigate("/purchases");
        } else {
          navigate("/restaurants");
        }
        setForeceReload(!foreceReload);
        return;
      })
      .catch(() => {
        return logout();
        // not authenticated, stay on the login page
      });
  }, [checkAuth, navigate, logout, foreceReload]);

  const onSubmit = async (data) => {
    try {
      setError("");
      setLoading(true);
      await login(
        {
          username: data.email,
          password: data.password,
        },
        "/"
      );
    } catch (error) {
      if (error.message === ERROR_CODES?.ACCOUNT_NOT_CONFIRMED) {
        return setError(
          "Tu cuenta aún no está activa. Revisa tu correo para confirmarla."
        );
      }
      if (error.message === ERROR_CODES?.ROLE_NOT_ALLOWED) {
        return setError("Tu cuenta no tiene permisos para acceder al panel.");
      }
      setError("Correo electrónico o contraseña incorrectos");
    } finally {
      setLoading(false);
    }
  };

  return (
    <LightSurface>
      <div
        className="min-h-screen w-full flex items-center justify-center bg-yellow p-4"
        style={{ colorScheme: "light" }}
      >
        <Card
          className="p-8 form-shadow border-0 flex flex-col justify-between p-8 pt-10 gap-4 sm:w-[500px] w-full"
          style={{ borderRadius: "24px" }}
        >
          <div className="text-center sm:mb-6 flex flex-col gap-2 items-center justify-center">
            <LogoAvatar alt="Morfy Logo" className="w-16 h-16 mx-auto mb-4 " />
            <Title>Bienvenido a Morfy</Title>
            <Subtitle>Ingresa tus datos para acceder a tu cuenta</Subtitle>
          </div>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-4 sm:gap-8"
          >
            <div className="">
              {error && <Error errorMessage={error} />}
              <FormField
                label="Correo electrónico"
                placeholder="nombre@tulocal.com"
                register={register("email")}
                error={errors.email}
                className="py-0"
              />
              <PasswordInput
                register={register("password")}
                error={errors.password}
                placeholder="**********"
                className="py-0"
              />
            </div>
            <div className="flex flex-col gap-6">
              <ThemedPrimaryButton type="submit" disabled={loading}>
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="h-5 w-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  </div>
                ) : (
                  "Iniciar sesión"
                )}
              </ThemedPrimaryButton>
              <div className="text-center text-sm flex flex-row gap-2 items-center justify-center">
                <span className="text-neutralStrongBlack font-semibold">
                  ¿No tienes una cuenta?
                </span>
                <a href="/#/register" className="text-darkYellow font-medium">
                  Regístrate
                </a>
              </div>
            </div>
          </form>
        </Card>
      </div>
    </LightSurface>
  );
};

export default CustomLogin;
